import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH, passwordResetConfirmSchema, messages } from '@nesc/shared';
import { api, errorMessage } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { AuthShell } from './auth-shell';

/** S03 パスワード再設定。トークンがあれば新パスワード設定、なければ送信フォーム */
export const PasswordResetPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const requestReset = async (event: FormEvent) => {
    event.preventDefault();
    setIsPending(true);
    setFormError(null);
    try {
      await api.post('/auth/password-reset/request', { email });
      // 存在しないアドレスでも同じ応答を返す
      setSent(true);
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setIsPending(false);
    }
  };

  const confirmReset = async (event: FormEvent) => {
    event.preventDefault();
    setErrors({});
    setFormError(null);

    const parsed = passwordResetConfirmSchema.safeParse({ token, password, passwordConfirm });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) fieldErrors[issue.path.join('.')] = issue.message;
      setErrors(fieldErrors);
      return;
    }

    setIsPending(true);
    try {
      await api.post('/auth/password-reset/confirm', parsed.data);
      navigate('/login', { replace: true });
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setIsPending(false);
    }
  };

  if (token) {
    return (
      <AuthShell title={messages.auth.resetConfirmTitle}>
        <form onSubmit={confirmReset} className="space-y-4" noValidate>
          {formError && (
            <p role="alert" className="rounded-control border-2 border-foreground bg-muted p-3 text-body font-semibold">
              {formError}
            </p>
          )}
          <Field label={messages.auth.password} required error={errors.password} hint={messages.auth.passwordRule}>
            {(props) => (
              <Input
                {...props}
                type="password"
                autoComplete="new-password"
                autoFocus
                minLength={PASSWORD_MIN_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>
          <Field label={messages.auth.passwordConfirm} required error={errors.passwordConfirm}>
            {(props) => (
              <Input
                {...props}
                type="password"
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" size="lg" className="w-full" disabled={isPending}>
            {isPending ? messages.common.loading : messages.auth.resetConfirmSubmit}
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={messages.auth.resetRequestTitle}>
      {sent ? (
        <div className="space-y-4 text-center">
          <p className="text-body text-foreground">{messages.auth.resetRequestDone}</p>
          <Button variant="outline" size="lg" className="w-full" onClick={() => navigate('/login')}>
            {messages.common.back}
          </Button>
        </div>
      ) : (
        <form onSubmit={requestReset} className="space-y-4" noValidate>
          {formError && (
            <p role="alert" className="rounded-control border-2 border-foreground bg-muted p-3 text-body font-semibold">
              {formError}
            </p>
          )}
          <Field label={messages.auth.email} required>
            {(props) => (
              <Input
                {...props}
                type="email"
                inputMode="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            )}
          </Field>
          <Button type="submit" size="lg" className="w-full" disabled={isPending}>
            {isPending ? messages.common.loading : messages.auth.resetRequestSubmit}
          </Button>
          <div className="text-center">
            <Button variant="link" size="sm" type="button" onClick={() => navigate('/login')}>
              {messages.common.back}
            </Button>
          </div>
        </form>
      )}
    </AuthShell>
  );
};
